import React, { useMemo } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import Select, { createFilter } from 'react-select';
import Input from '../../../common/form-elements/input/input';
import Textarea from '../../../common/form-elements/textarea/textarea';
import FormErrorMessage from '../../../common/form-elements/form-error-message/form-error-message';
import DateInput from '../../../common/form-elements/date-input/DateInput';
import UserService from '../../../../services/user-service';
/* Validations */
import { maxLength, minLength, required, requiredSelect, schemaValidate } from '../../../../helpers/validation-helpers';

import {
    TASK_PRIORITIES,
    TASK_STATUSES,
    TASK_LIST_TITLE_LABEL,
    TASK_LIST_TITLE_PLACEHOLDER,
    TASK_LIST_DESCRIPTION_LABEL,
    TASK_LIST_DESCRIPTION_PLACEHOLDER,
    TASK_LIST_ASSIGNED_TO_LABEL,
    TASK_LIST_STATUS_LABEL,
    TASK_LIST_PRIORITY_LABEL,
    TASK_LIST_TARGET_DATE
} from './constants';

const INITIAL_VALUES = {
    name: '',
    description: '',
    assignedTo: '',
    status: TASK_STATUSES[0],
    priority: TASK_PRIORITIES[0],
    targetDate: ''
};

const TaskListNewTaskForm = ({
    onSubmit,
    closeForm,
    userOptions,
    selectedTask
}) => {
    const createInitialFormValues = ({
        name,
        description,
        status,
        priority,
        assignedUserFirstName,
        assignedUserLastName,
        assignedUserId,
        targetDate
    }) => ({
        name,
        description,
        status: TASK_STATUSES.find(s => s.value === status),
        priority: TASK_PRIORITIES.find(p => p.value === priority),
        assignedTo: {
            value: assignedUserId,
            label: `${assignedUserFirstName} ${assignedUserLastName}`
        },
        targetDate: targetDate ? new Date(targetDate) : null
    });

    const formInitialValues = useMemo(
        () => {
            INITIAL_VALUES.assignedTo = userOptions.find(u => u.value === UserService.getLoggedUser().getId());
            // eslint-disable-next-line no-underscore-dangle
            return !selectedTask ? INITIAL_VALUES : createInitialFormValues(selectedTask._pristineProperties);
        },
        [selectedTask, userOptions]
    );

    const handleFormSubmit = values => {
        const success = onSubmit(values);
        if (success) {
            closeForm();
        }
    };

    const handleFormSelectChange = (props, field) => value => {
        props.setFieldValue(field, value);
    };

    return (
        <Formik
            initialValues={formInitialValues}
            onSubmit={handleFormSubmit}
        >
            {(props) => {
                return (
                    <Form>
                        <div className="glu-modal__body">
                            <div className="glu-content__section">
                                <div className="glu-form-row">
                                    <div className="glu-label">{TASK_LIST_TITLE_LABEL}</div>
                                    <Field
                                        as={Input}
                                        name="name"
                                        className="glu-form-input-pad"
                                        autoFocus
                                        placeholder={TASK_LIST_TITLE_PLACEHOLDER}
                                        onChange={props.handleChange('name')}
                                        validate={schemaValidate(
                                            [required(), minLength(), maxLength(30)]
                                        )}
                                        invalid={
                                            props.errors.name
                                            && props.touched.name
                                        }
                                        value={props.values.name}
                                    />
                                    <ErrorMessage name="name" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <label className="glu-label">{TASK_LIST_DESCRIPTION_LABEL}</label>
                                    <Field
                                        as={Textarea}
                                        onChange={props.handleChange('description')}
                                        placeholder={TASK_LIST_DESCRIPTION_PLACEHOLDER}
                                        name="description"
                                        validate={schemaValidate(maxLength(250))}
                                        invalid={
                                            props.errors.description
                                                    && props.touched.description
                                        }
                                        maxChar={250}
                                        className="glu-form-input-pad"
                                    />
                                    <ErrorMessage
                                        name="description"
                                        component={FormErrorMessage}
                                    />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-label">{TASK_LIST_ASSIGNED_TO_LABEL}</div>
                                    <Field
                                        as={Select}
                                        name="assignedTo"
                                        aria-label="assigned-select"
                                        value={props.values.assignedTo}
                                        onChange={handleFormSelectChange(props, 'assignedTo')}
                                        validate={schemaValidate([requiredSelect()])}
                                        options={userOptions}
                                        invalid={
                                            props.errors.assignedTo
                                            && props.touched.assignedTo
                                        }
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        filterOption={createFilter({
                                            matchFrom: 'any',
                                            stringify: option => `${option.label}`,
                                        })}
                                    />
                                    <ErrorMessage name="assignedTo" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-label">{TASK_LIST_STATUS_LABEL}</div>
                                    <Field
                                        as={Select}
                                        name="status"
                                        aria-label="status-select"
                                        value={props.values.status}
                                        options={TASK_STATUSES}
                                        onChange={handleFormSelectChange(props, 'status')}
                                        validate={schemaValidate([requiredSelect()])}
                                        invalid={
                                            props.errors.status
                                            && props.touched.status
                                        }
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        filterOption={createFilter({
                                            matchFrom: 'any',
                                            stringify: option => `${option.label}`,
                                        })}
                                    />
                                    <ErrorMessage name="status" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <div
                                        aria-label="priority-label"
                                        className="glu-label"
                                    >
                                        {TASK_LIST_PRIORITY_LABEL}
                                    </div>
                                    <Field
                                        as={Select}
                                        name="priority"
                                        placeholder="Priority"
                                        value={props.values.priority}
                                        options={TASK_PRIORITIES}
                                        onChange={handleFormSelectChange(props, 'priority')}
                                        validate={schemaValidate([requiredSelect()])}
                                        invalid={
                                            props.errors.priority
                                            && props.touched.priority
                                        }
                                        className="glu-select glu-form-input-pad"
                                        classNamePrefix="react-select"
                                        filterOption={createFilter({
                                            matchFrom: 'any',
                                            stringify: option => `${option.label}`,
                                        })}
                                    />
                                    <ErrorMessage name="priority" component={FormErrorMessage} />
                                </div>

                                <div className="glu-form-row">
                                    <div className="glu-label">{TASK_LIST_TARGET_DATE}</div>
                                    <Field
                                        name="targetDate"
                                        component={DateInput}
                                    />
                                </div>
                            </div>
                        </div>

                        <footer className="glu-modal__footer">

                            <div className="glu-flex-grow glu-flex-row">
                                <div className="glu-flex-grow text-right">
                                    <button
                                        onClick={closeForm}
                                        className="glu-btn glu-btn--secondary"
                                        type="button"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <div className="glu-flex-shrink">
                                    <button
                                        className="glu-btn glu-btn--primary"
                                        type="submit"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </footer>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default TaskListNewTaskForm;
